import cls from 'classnames';
import { Field, FormikProps, FieldArray, ArrayHelpers, FormikErrors } from 'formik';
import * as React from 'react';

import { TwsAdmin, AmenitiesFormData } from './types';
import TextInput from '../input-text';
import Tooltip from 'rc-tooltip';
import { arrayToRows } from '../../utils/arrays';
import { SpinnerOverlay } from '@weddingspot/ws-core-components';
import { WsFormikError } from '../../utils/formik';

import CheckboxInput from '../input-checkbox';

export enum AmenityType {
    AMENITY = 1,
    RESTRICTION,
}

interface OwnProps {
    amenityType: AmenityType;
    choicesLoaded: boolean | null;
    choicesData: TwsAdmin.AmenitiesFormChoices | null;
    extraAmenities: TwsAdmin.ExtraAmenity[];
    valuesLoaded: boolean | null;
    formikProps: FormikProps<AmenitiesFormData>;

    handleCreateExtraAmenity: (name: string) => Promise<void>;
    handleDeleteExtraAmenity: (amenity: TwsAdmin.ExtraAmenity) => Promise<void>;
}

interface OwnState {
    deletingExtraAmenityIds: number[];
    addingAmenity: boolean;
    submittingAmenity: boolean;
}

export default class AmenitiesCard extends React.Component<OwnProps, OwnState> {
    constructor(props: OwnProps) {
        super(props);

        this.state = {
            deletingExtraAmenityIds: [],
            addingAmenity: false,
            submittingAmenity: false,
        };
    }

    render() {
        const { amenityType, choicesLoaded, choicesData, extraAmenities, valuesLoaded, formikProps } = this.props;

        const dataFullyLoaded = choicesLoaded && !!choicesData && valuesLoaded;

        let choices: TwsAdmin.BaseChoiceOption<number>[] = [];
        if (!!choicesData) {
            let key = '';
            if (amenityType === AmenityType.RESTRICTION) {
                key = 'restrictions';
            } else {
                key = 'amenities';
            }

            choices = (choicesData[key] as TwsAdmin.BaseChoicesData<number>).choices;

            // append the extra_* "choices" (these aren't editable.. they're just always checked)
            let extraChoices = extraAmenities.map((choice) => ({ ...choice, isExtra: true }));
            choices = choices.concat(extraChoices);
        }

        const fieldName = amenityType === AmenityType.RESTRICTION ? 'restrictions' : 'amenities';
        const topLevelError = typeof formikProps.errors[fieldName] === 'string' ? formikProps.errors[fieldName] : null;

        return (
            <div className='VenueManagement--card VenueManagement--amenities'>
                <div className='VenueManagement--card--header'>
                    {amenityType === AmenityType.RESTRICTION ? 'Restrictions' : 'Amenities'}
                </div>
                <div className='VenueManagement--card--subheader'>
                    {amenityType === AmenityType.RESTRICTION
                        ? 'Set restrictions and requirements for this venue.'
                        : 'Select all amenities that are included in the package at no additional cost.'}
                </div>
                {((choicesLoaded && !choicesData) || valuesLoaded === false) && (
                    <div className='VenueManagement--card--body VenueManagement--form-level-error'>
                        Could not load form data, please try again later
                    </div>
                )}
                {!!topLevelError && <div className='VenueManagement--form-level-error'>{topLevelError}</div>}
                {dataFullyLoaded && (
                    <div className='VenueManagement--card--body'>
                        <div className='VenueManagement--card--section VenueManagement--amenities--checkboxes'>
                            <FieldArray
                                name={fieldName}
                                render={(arrayHelpers) => {
                                    return arrayToRows(choices, 3).map((row, rowIdx) => (
                                        <div key={rowIdx} className='VenueManagement--row'>
                                            {row.map((col, colIdx) => (
                                                <div key={colIdx} className='VenueManagement--col'>
                                                    {!!col &&
                                                        (!col['isExtra']
                                                            ? this.renderAmenitiesRow(col, choices, fieldName, arrayHelpers)
                                                            : this.renderExtraAmenitiesRow(col))}
                                                </div>
                                            ))}
                                        </div>
                                    ));
                                }}
                            />
                        </div>
                        {this.state.addingAmenity && this.renderAddExtraAmenity(formikProps)}
                        <div className=''>
                            <a
                                className={cls('VenueManagement--brand-link', {
                                    'VenueManagement--brand-link__disabled': this.state.addingAmenity,
                                })}
                                onClick={() => {
                                    if (!this.state.addingAmenity) {
                                        this.setState({ addingAmenity: true });
                                    }
                                }}
                            >
                                + Add {amenityType === AmenityType.RESTRICTION ? 'a restriction' : 'an amenity'}
                            </a>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private renderAmenitiesRow(
        choice: TwsAdmin.BaseChoiceOption<number>,
        allChoices: TwsAdmin.BaseChoiceOption<number>[],
        fieldName: string,
        arrayHelpers: ArrayHelpers
    ) {
        return (
            <Field
                label={choice.label}
                component={CheckboxInput}
                name={`fieldName.${allChoices.indexOf(choice)}`}
                value={choice.value}
                checked={this.props.formikProps.values[fieldName].indexOf(choice.value) > -1}
                onChange={(e: React.SyntheticEvent<HTMLInputElement>) => {
                    if ((e.target as HTMLInputElement).checked) {
                        arrayHelpers.push(choice.value);
                    } else {
                        const idx = this.props.formikProps.values[fieldName].indexOf(choice.value);
                        arrayHelpers.remove(idx);
                    }
                }}
                hideErrors={true}
            />
        );
    }

    private renderExtraAmenitiesRow(choice: TwsAdmin.BaseChoiceOption<number>) {
        return (
            <SpinnerOverlay
                isSpinning={this.state.deletingExtraAmenityIds.indexOf(choice.value) > -1}
                spinnerProps={{
                    spinnerColor: 'black',
                    spinnerDiameter: 25,
                }}
            >
                <div className='VenueManagement--deleteable-checkbox-row'>
                    <Tooltip
                        prefixCls='rc-tooltip__dark'
                        placement='top'
                        trigger={['hover']}
                        mouseEnterDelay={1.0}
                        overlay={<span>{choice.label}</span>}
                    >
                        <span>
                            <Field
                                label={choice.label}
                                component={CheckboxInput}
                                name='__IGNORE__'
                                value={choice.value}
                                checked={true}
                                hideErrors={true}
                            />
                        </span>
                    </Tooltip>
                    <span
                        className='icon-hb-nx-trash VenueManagement--deleteable-checkbox-row--icon'
                        onClick={() => {
                            const updated = this.state.deletingExtraAmenityIds.concat(choice.value);
                            this.setState({ deletingExtraAmenityIds: updated });
                            this.props.handleDeleteExtraAmenity(choice).then(() => {
                                const removed = this.state.deletingExtraAmenityIds.filter((val) => val !== choice.value);
                                this.setState({ deletingExtraAmenityIds: removed });
                            });
                        }}
                    />
                </div>
            </SpinnerOverlay>
        );
    }

    private renderAddExtraAmenity(formikProps: FormikProps<any>) {
        const tmpFieldName = '__newExtraAmenity';

        const handleCreateAmenity = () => {
            this.setState({ submittingAmenity: true });
            this.props
                .handleCreateExtraAmenity(formikProps.values[tmpFieldName])
                .then(() => {
                    formikProps.setFieldValue(tmpFieldName, '');
                    this.setState({
                        submittingAmenity: false,
                        addingAmenity: false,
                    });
                })
                .catch((err: FormikErrors<TwsAdmin.ExtraAmenitiesWriteData> | string) => {
                    if (typeof err === 'string') {
                        formikProps.setFieldError(tmpFieldName, err);
                    } else if (!!err.extra_amenities) {
                        let _err = err as WsFormikError<TwsAdmin.ExtraAmenitiesWriteData>;
                        // stupid check but typescript compiler complains without it
                        if (!!_err.extra_amenities) {
                            const __errs = _err.extra_amenities[0] as WsFormikError<TwsAdmin.ExtraAmenityWriteData>;
                            if (!!__errs && !!__errs.name) {
                                formikProps.setFieldError(tmpFieldName, __errs.name);
                            }
                        }
                    } else {
                        formikProps.setFieldError(tmpFieldName, 'An unknown error occurred, please try again later.');
                    }

                    this.setState({
                        submittingAmenity: false,
                    });
                });
        };

        return (
            <div className='VenueManagement--row'>
                <SpinnerOverlay isSpinning={this.state.submittingAmenity}>
                    <div className='VenueManagement--amenities--field-container'>
                        <Field
                            label={this.props.amenityType === AmenityType.RESTRICTION ? 'Restrictions' : 'Amenities'}
                            component={TextInput}
                            name={tmpFieldName}
                            onKeyPress={(e: React.KeyboardEvent) => {
                                // Eat the enter event on keypress to prevent it
                                // from submitting the outer form
                                if (e.key === 'Enter') {
                                    handleCreateAmenity();
                                    e.preventDefault();
                                }
                            }}
                        />
                        <button
                            className='nx-button nx-button--small'
                            type='button'
                            onClick={() => {
                                handleCreateAmenity();
                            }}
                        >
                            Add
                        </button>
                        <span className='icon-hb-nx-close' onClick={() => this.setState({ addingAmenity: false })} />
                    </div>
                </SpinnerOverlay>
            </div>
        );
    }
}
